import { useRef, useState } from 'react'
import { FileUp, Loader2, Paperclip, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/lib/api/client'
import { toApiProblem } from '@/lib/api/client'
import { cn } from '@/lib/utils'

export interface UploadedFile {
  id: string
  filename: string
  contentType: string | null
  sizeBytes: number
  ownerType: string | null
  ownerId: string | null
  uploadedAt: string
}

export interface FileUploaderProps {
  /** What the file belongs to; sent so the backend can find it by owner later. */
  ownerType?: string
  ownerId?: string
  /** Already-attached files, rendered above the picker. */
  files?: UploadedFile[]
  onUploaded?: (file: UploadedFile) => void
  onRemoved?: (id: string) => void
  /** Comma-separated accept list. Defaults to what the server's allowlist permits. */
  accept?: string
  disabled?: boolean
  className?: string
}

/**
 * The list of what the server actually accepts.
 *
 * <p>Mirrors `FileService.ALLOWED_CONTENT_TYPES`. The server is the authority — this only
 * decides what the picker offers, so a mismatch means an inconvenient dialog rather than an
 * accepted upload. HTML and SVG are absent from both, on purpose: each executes script, and
 * downloads are served from the application's own origin.
 */
const DEFAULT_ACCEPT = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/tiff', 'image/heic',
  'text/plain', 'text/csv',
  '.doc', '.docx', '.xls', '.xlsx', '.zip',
].join(',')

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Attaching files to a record.
 *
 * <p>Uploads one at a time and reports each result, rather than firing a batch and showing a
 * single outcome: when one of five files is the wrong type, "upload failed" tells nobody
 * which one.
 */
export function FileUploader({
  ownerType,
  ownerId,
  files = [],
  onUploaded,
  onRemoved,
  accept = DEFAULT_ACCEPT,
  disabled,
  className,
}: FileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  async function send(selected: FileList | null) {
    if (!selected || selected.length === 0) return
    setError(null)
    setBusy(true)
    try {
      for (const file of Array.from(selected)) {
        const body = new FormData()
        body.append('file', file)
        const { data } = await apiClient.post<UploadedFile>('/v1/files', body, {
          params: { ownerType, ownerId },
        })
        onUploaded?.(data)
      }
    } catch (caught) {
      // The server's own message names the file type it refused; a generic string would not.
      setError(toApiProblem(caught)?.detail ?? 'That file could not be uploaded')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className={cn('space-y-3', className)}>
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-2 rounded-[var(--radius-control)] border border-[var(--color-border)] px-3 py-2 text-sm"
            >
              <Paperclip className="size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
              <a
                href={`/api/v1/files/${file.id}`}
                className="min-w-0 flex-1 truncate text-[var(--color-primary)] hover:underline"
              >
                {file.filename}
              </a>
              <span className="shrink-0 text-xs text-[var(--color-muted)]">{humanSize(file.sizeBytes)}</span>
              {onRemoved && !disabled && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${file.filename}`}
                  onClick={() => onRemoved(file.id)}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div
        onDragOver={(event) => { event.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          if (!disabled) void send(event.dataTransfer.files)
        }}
        className={cn(
          'rounded-[var(--radius-control)] border border-dashed p-4 text-center transition-colors',
          dragging ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]' : 'border-[var(--color-border)]',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          disabled={disabled || busy}
          className="sr-only"
          // Labelled rather than hidden behind a button alone, so the control is reachable
          // by keyboard and announced as a file input.
          aria-label="Choose files to upload"
          onChange={(event) => void send(event.target.files)}
        />
        <Button
          type="button"
          variant="secondary"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileUp className="size-4" aria-hidden="true" />}
          {busy ? 'Uploading…' : 'Choose files'}
        </Button>
        <p className="mt-2 text-xs text-[var(--color-muted)]">or drop them here</p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  )
}
